"use client";
import React, { useMemo } from "react";

import { usePathname, useSearchParams } from "next/navigation";

import { getPageNumberFromMap } from "~/components/plp-page/hooks/use-request-handler/helpers/get-page-number-from-map";
import { getPaginationUrls } from "~/helpers/get-pagination-urls";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Nullable } from "~/types/general.types";

import { CombinedButton } from "../combined-button/component";

import { DEFAULT_LOAD_PREVIOUS_BUTTON_TEXT } from "./constants";

export interface LoadPreviousButtonProps {
  disabled?: boolean;
  pageSize?: number;
  onClick?: () => void;
  loadPreviousButtonText?: Nullable<string>;
  itemsMap?: Map<string, number>;
}

export const LoadPreviousButton: React.FC<LoadPreviousButtonProps> = ({
  disabled,
  pageSize,
  onClick,
  loadPreviousButtonText,
  itemsMap,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const loadPreviousButtonTextUpdated = useMemo(
    () =>
      (loadPreviousButtonText || DEFAULT_LOAD_PREVIOUS_BUTTON_TEXT).replace(
        "{{number}}",
        pageSize?.toString() || "",
      ),
    [loadPreviousButtonText, pageSize],
  );

  const prevPage = getPageNumberFromMap(itemsMap) - 1;
  const prevLink = getPaginationUrls({
    prevPage,
    pathname,
    searchParams,
  }).prev;

  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick && onClick();
  };

  return (
    <CombinedButton
      id="load-previous-button"
      href={prevLink}
      variant="outlined"
      size="large"
      onClick={handleOnClick}
      disabled={disabled}
      {...getTestAutomationProps("load-previous-button")}
    >
      {loadPreviousButtonTextUpdated}
    </CombinedButton>
  );
};
